import {useAppDispatch, useAppSelector} from "@/lib/store.ts";
import {useEffect} from "react";
import {fetchUser} from "@/lib/store/auth.slice.ts";
import {fetchOrganizations} from "@/lib/store/organization.slice.ts";
import {fetchMembership} from "@/lib/store/membership.slice.ts";
import {loadPermissionAwareMenu, loadScopeWithoutOrganization} from "@/lib/config.slice.ts";
import * as Sentry from "@sentry/react";
import { sentryEnabled } from "@/instrument";

export function AuthInit() {
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.auth);
    const { activeOrgId, organizations, loading: organizationLoading } = useAppSelector((s) => s.organization);
    const { membership, loading: membershipLoading } = useAppSelector((s) => s.membership);

    useEffect(() => {
        if (!sentryEnabled) return;

        if (user?.id) {
            Sentry.setUser({ id: user.id });
            Sentry.setTag("organization_id", activeOrgId ?? "none");
            Sentry.setContext("organization", { id: activeOrgId ?? null });
            return;
        }

        Sentry.setUser(null);
        Sentry.setTag("organization_id", "none");
        Sentry.setContext("organization", null);
    }, [user?.id, activeOrgId]);

    useEffect(() => {
        dispatch(fetchUser());
    }, [dispatch]);

    useEffect(() => {
        if (user?.id) {
            dispatch(fetchOrganizations());
        }
    }, [user?.id, dispatch]);

    useEffect(() => {
        if (user?.id && activeOrgId) {
            dispatch(fetchMembership());
        }
    }, [user?.id, activeOrgId, dispatch]);

    useEffect(() => {
        // Load menu when we have an active organization, even if membership fetch failed
        // This handles cases where individuals might not have membership data yet
        if (activeOrgId && !membershipLoading && !organizationLoading && organizations.length > 0) {
            const activeOrg = organizations.find(org => org.id === activeOrgId);
            if (activeOrg) {
                dispatch(loadPermissionAwareMenu(activeOrg.type));
            }
        } else if (user?.id && !organizationLoading && !activeOrgId && organizations.length === 0) {
            dispatch(loadScopeWithoutOrganization(user.id));
        }
    }, [user?.id, activeOrgId, membershipLoading, organizationLoading, organizations, dispatch]);

    return null;
}
